import { useEffect, useMemo, useRef } from 'react';
import { useMap } from '@vis.gl/react-google-maps';
import { isValidCenter } from '../../utils/isValidCenter';
import { haversineMi } from '../../utils/haversine';
import { riskScoreToColor } from './mapUtils';
import { ZCTA_COVERAGE_RADIUS_MI } from '../../utils/zctaScoring';

const GEOJSON_CENTROID_CACHE = new WeakMap();

function buildRadiusBoundingBox(centerLat, centerLng, radiusMi) {
  const latDelta = radiusMi / 69;
  const lngDelta = radiusMi / (Math.cos((centerLat * Math.PI) / 180) * 69 || 1);
  return {
    centerLat,
    centerLng,
    minLat: centerLat - latDelta,
    maxLat: centerLat + latDelta,
    minLng: centerLng - lngDelta,
    maxLng: centerLng + lngDelta,
  };
}

function isPointInBoundingBox(lat, lng, box) {
  return lat >= box.minLat && lat <= box.maxLat && lng >= box.minLng && lng <= box.maxLng;
}

function toCentroid(value) {
  const parsed = parseFloat(String(value ?? '').replace(/^\+/, ''));
  return Number.isFinite(parsed) ? parsed : null;
}

function getCachedFeatureCentroids(fullGeoJson) {
  if (!fullGeoJson?.features) return [];

  const cached = GEOJSON_CENTROID_CACHE.get(fullGeoJson);
  if (cached) return cached;

  const prepared = fullGeoJson.features
    .map((feature) => {
      const props = feature?.properties || {};
      const centroidLat = toCentroid(props.INTPTLAT20);
      const centroidLng = toCentroid(props.INTPTLON20);
      if (centroidLat == null || centroidLng == null) return null;
      return {
        feature,
        centroidLat,
        centroidLng,
      };
    })
    .filter(Boolean);

  GEOJSON_CENTROID_CACHE.set(fullGeoJson, prepared);
  return prepared;
}

/** When enabled is false, layer stays mounted but adds no data/listeners so Map child tree stays stable. */
export default function ZctaCrimeLayer({
  enabled = true,
  zctaData,
  fullGeoJson,
  workCenter,
  homeCenter,
  opacity,
  onAreaClick = null,
}) {
  const map = useMap();
  const onAreaClickRef = useRef(onAreaClick);
  onAreaClickRef.current = onAreaClick;

  const [workLat, workLng] = workCenter ?? [];
  const [homeLat, homeLng] = homeCenter ?? [];
  const hasWorkCenter = isValidCenter(workCenter);
  const hasHomeCenter = isValidCenter(homeCenter);

  const filteredGeoJson = useMemo(() => {
    if (!enabled || !fullGeoJson?.features || (!hasWorkCenter && !hasHomeCenter)) {
      return null;
    }

    const centerBoxes = [];
    if (hasWorkCenter) centerBoxes.push(buildRadiusBoundingBox(workLat, workLng, ZCTA_COVERAGE_RADIUS_MI));
    if (hasHomeCenter) centerBoxes.push(buildRadiusBoundingBox(homeLat, homeLng, ZCTA_COVERAGE_RADIUS_MI));

    const prepared = getCachedFeatureCentroids(fullGeoJson);
    const features = prepared
      .filter(({ centroidLat, centroidLng }) =>
        centerBoxes.some((box) =>
          isPointInBoundingBox(centroidLat, centroidLng, box)
          && haversineMi(box.centerLat, box.centerLng, centroidLat, centroidLng) <= ZCTA_COVERAGE_RADIUS_MI
        )
      )
      .map(({ feature }) => feature);

    return { type: 'FeatureCollection', features };
  }, [enabled, fullGeoJson, hasHomeCenter, hasWorkCenter, homeLat, homeLng, workLat, workLng]);

  const styleResolver = useMemo(() => {
    const clampedOpacity = Math.max(0, Math.min(1, Number(opacity)));
    const styleByZcta = new Map();
    const fallbackFill = riskScoreToColor(null);
    const fallbackStyle = {
      fillColor: fallbackFill,
      strokeColor: fallbackFill,
      fillOpacity: clampedOpacity,
      strokeWeight: 1,
      strokeOpacity: 0.6,
    };

    return (feature) => {
      const zcta = feature.getProperty('GEOID20') ?? feature.getProperty('ZCTA5CE20') ?? '';
      if (!zcta) return fallbackStyle;

      let style = styleByZcta.get(zcta);
      if (style) return style;

      const riskEntry = zctaData?.[zcta];
      const fillColor = riskScoreToColor(riskEntry?.risk_score ?? null);
      style = {
        fillColor,
        strokeColor: fillColor,
        fillOpacity: clampedOpacity,
        strokeWeight: 1,
        strokeOpacity: 0.6,
      };
      styleByZcta.set(zcta, style);
      return style;
    };
  }, [opacity, zctaData]);

  // Heavy path: only rebuild data layer when source features actually change.
  useEffect(() => {
    if (!map) return;

    map.data.forEach((f) => map.data.remove(f));

    if (!enabled || !filteredGeoJson) return;
    map.data.addGeoJson(filteredGeoJson);

    return () => {
      map.data.forEach((f) => map.data.remove(f));
    };
  }, [enabled, filteredGeoJson, map]);

  // Cheap path: style updates should not force a full GeoJSON re-add.
  useEffect(() => {
    if (!enabled || !map || !filteredGeoJson) return;
    map.data.setStyle(styleResolver);
  }, [enabled, filteredGeoJson, map, styleResolver]);

  // Shaded areas swallow map clicks, so forward their taps to the same handler as the rest of the map.
  useEffect(() => {
    if (!enabled || !map || !filteredGeoJson) return;

    const clickListener = map.data.addListener('click', (event) => {
      onAreaClickRef.current?.(event.latLng.lat(), event.latLng.lng());
    });

    return () => {
      window.google.maps.event.removeListener(clickListener);
    };
  }, [enabled, filteredGeoJson, map]);

  return null;
}
